import { Button } from "@/components/ui/button";
import Drawer from "@/components/common/Drawer/Drawer";
import { IMenuTypes, isIMenuButton, isIMenuLink } from "@/utils/models/core";

import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

import { Bars3Icon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "@/hooks/useAuth";
import { SvgClockIn, SvgInfo, SvgLogoWhite } from "@/components/SvgIcon";
import Modal from "@/components/common/Modal/Modal";
import Camera, { ICamera } from "@/components/feature/Camera/Camera";
import { API_URL } from "@/utils/api/config";

// import BadgeStatus from "@/components/feature/Badge/Badge";
import { useTsRestQueryClient } from "@ts-rest/react-query";
import { opusClient } from "@/utils/rest-client/opus-client";
import { format, parseISO } from "date-fns";
import Activity from "@/components/feature/Activity";
import UpdateStatus from "@/components/feature/UpdateStatus/UpdateStatus";
// import { TStatus } from "@/utils/types/app";
import storage from "@/utils/api/storage";

import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";

const HeaderMobile = () => {
	const { logout, user, getMe } = useAuth();
	const apiQueryClient = useTsRestQueryClient(opusClient);
	const navigate = useNavigate();
	const [statusOpen, setStatusOpen] = useState<boolean>(false);
	const [attendanceOpen, setAttendanceOpen] = useState<boolean>(false);
	const [openPop, setOpenPop] = useState<boolean>(false);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const isAdminView = storage.getItem("accessStatus") === "Admin";

	const menuHeader: IMenuTypes[] = [
		{
			label: "Overview",
			to: "/dashboard",
		},
		{
			label: "Timesheet",
			to: "/attendance",
		},
		{
			label: "Leave Management",
			to: "/leave-management",
		},
		{
			label: "Team Calendar",
			to: "/team-calendar",
		},
		{
			label: "Team Directory",
			to: "/team-directory",
		},
		{
			label: "Company Resources",
			to: "/company-resources",
		},
		{
			label: "My profile",
			to: "/profile",
		},
		{
			label: "Settings",
			to: "/company-settings",
			isAdminOnly: true,
		},
		{
			label: "Logout",
			onClick: () => {
				logout();
				navigate("/login", { replace: true });
			},
		},
	];

	const { data: latestTimeLog, refetch: refetchLatestTimeLog } =
		apiQueryClient.users.getLatestUserAttendances.useQuery(
			["latest-attendance"],
			{ params: { userId: user!.id } },
			{
				queryKey: ["latest-attendance"],
				retry: (failureCount, error) => {
					if (error instanceof Error) {
						console.log(error?.stack);
					}
					return failureCount < 3 && error.status !== 404;
				},
			}
		);

	const isToday =
		latestTimeLog &&
		format(parseISO(latestTimeLog!.body!.day), "yyyy-MM-dd ") ===
		format(new Date(), "yyyy-MM-dd ");

	const purpose: ICamera["purpose"] =
		!latestTimeLog || (latestTimeLog && !isToday) ? "Clock In" : "Clock Out";

	const log = [
		purpose == "Clock In"
			? {
				name: "Clock in",
				description:
					"Make sure to log your hours to ensure you're marked as present.",
				href: "##",
				icons: <SvgClockIn className="w-5 h-5" />,
				buttons: (
					<Button
						variant="default"
						className="w-28 rounded-2xl bg-opus-blue-100 !py-1 !px-2 h-8"
						disabled={latestTimeLog?.body.in && isToday}
						onClick={() => setAttendanceOpen(true)}
					>
						Clock In
					</Button>
				),
			}
			: {
				name: "Clock out",
				description:
					"Our clients depend on our availability during business hours. By staying until at least 3 PM, we can address their needs effectively.",
				href: "##",
				icons: <SvgClockIn className="w-5 h-5" />,
				buttons: (
					<Button
						variant="default"
						className="w-28 rounded-2xl bg-opus-blue-100 !py-1 !px-2 h-8"
						disabled={!latestTimeLog?.body.in && !isToday}
						onClick={() => setAttendanceOpen(true)}
					>
						Clock Out
					</Button>
				),
			},
		{
			name: "Update Attendance/Status",
			description:
				"Update your attendance or status so we can track your availability.",
			href: "##",
			icons: <SvgInfo className="w-5 h-5" />,
			buttons: (
				<Button
					variant="default"
					className="w-28 rounded-full bg-white text-opus-gray-800 hover:text-white  !py-1 !px-2 h-8"
					onClick={() => setStatusOpen(true)}
				>
					Update
				</Button>
			),
		},
	];

	useEffect(() => {
		getMe();
	}, [getMe]);

	return (
		<div className="relative w-full bg-opus-blue-100">
			<div className="relative px-3 sm:px-4 py-4 flex justify-between">
				<div className="flex items-center flex-1">
					<div className="text-white text-md">
						<SvgLogoWhite className="w-32" />
					</div>
				</div>
				<div className="flex justify-end">
					<Sheet open={openPop} onOpenChange={setOpenPop}>
						<SheetTrigger asChild>
							<div className="mr-2">
								<Button
									variant={"default"}
									className="bg-opus-blue-400 py-2 px-4  space-x-2 rounded-lg"
								>
									<SvgClockIn className="w-5 h-5 fill-current text-white mr-2" />
									{purpose}
								</Button>
							</div>
						</SheetTrigger>
						<SheetContent side={"bottom"} className="px-0">
							<SheetHeader>
								<SheetTitle className="text-center">Log Hours</SheetTitle>
							</SheetHeader>
							<div className="relative bg-white space-y-4 px-4">
								{log.map((item, index) => (
									<div key={index} className="bg-opus-gray-600 rounded-lg p-2 ">
										<div key={index} className="flex space-x-2">
											<div className=" rounded-full bg-white w-12 h-10 px-2 flex items-center justify-center ">
												{item.icons}
											</div>
											<div className="w-full">
												<div className="font-bold text-sm">{item.name}</div>
												<div className="text-xs mr-2 break-word">
													{item.description}
												</div>
											</div>
										</div>
										<div
											onClick={() => setOpenPop(false)}
											className="flex items-center mt-2 justify-center"
										>
											{item.buttons}
										</div>
									</div>
								))}
							</div>
						</SheetContent>
					</Sheet>
					{/* <div className="mx-2">
						<SvgNotification className="w-8 h-8" />
					</div> */}
					<Button
						variant="default"
						className="bg-inherit hover:bg-inherit p-0"
						onClick={() => setIsOpen(true)}
					>
						<Bars3Icon className="text-white w-7 h-7" />
					</Button>

					<Drawer size="full" isOpen={isOpen} setIsOpen={setIsOpen}>
						<div className="flex items-center justify-center mb-5">
							<div className="relative ">
								{user!.profilePicture ? (
									<div
										className={clsx(
											"bg-no-repeat bg-cover bg-center",
											"rounded-full h-10 w-10"
										)}
										style={{
											backgroundImage: `url(${API_URL}/files/${user!.profilePicture.id
												})`,
										}}
									/>
								) : (
									<div className="flex items-center justify-center bg-gray-200 rounded-full h-10 w-10">
										{user!.firstName && user!.lastName ? (
											<span className="text-gray-600 font-bold">
												{user!.firstName.charAt(0).toUpperCase()}
												{user!.lastName.charAt(0).toUpperCase()}
											</span>
										) : (
											<span className="text-gray-600 font-bold">?</span>
										)}
									</div>
								)}
								<div className="absolute top-7 right-0">
									<Activity activity={user!.userStatus!.activity} />
								</div>

								<div className="text-center mt-3">{user?.firstName}</div>
							</div>
						</div>
						<div className="">
							{user?.role?.title === "Admin" ? (
								<Button
									onClick={() => {
										if (storage.getItem("accessStatus") === "Staff") {
											storage.setItem("accessStatus", "Admin");
										} else {
											storage.setItem("accessStatus", "Staff");
										}
										window.location.reload();
									}}
									className="border-t w-full text-left block bg-transparent rounded-none text-inherit p-2 hover:bg-transparent"
									variant={"default"}
								>
									{`Switch to ${storage.getItem("accessStatus") === "Staff"
											? "Admin"
											: "Staff"
										}`}
								</Button>
							) : (
								user?.isLeaveApprover && (
									<Button
										onClick={() => {
											if (storage.getItem("accessStatus") === "Staff") {
												storage.setItem("accessStatus", "Approver");
											} else {
												storage.setItem("accessStatus", "Staff");
											}
											window.location.reload();
										}}
										className="border-t w-full text-left block bg-transparent rounded-none text-inherit p-2 hover:bg-transparent"
										variant={"default"}
									>
										{`Switch to ${storage.getItem("accessStatus") === "Staff"
												? "Approver"
												: "Staff"
											}`}
									</Button>
								)
							)}
							{menuHeader.map(
								(item: IMenuTypes, index: number) =>
									((item.isAdminOnly && isAdminView) || !item.isAdminOnly) && (
										<div key={index} className="border-t">
											{isIMenuLink(item) && (
												<Link
													to={item.to}
													onClick={() => setIsOpen(false)}
													className={clsx(
														item.className,
														"text-sm",
														"group flex items-center w-full px-2 py-2"
													)}
												>
													{item.icon}
													{item.label}
												</Link>
											)}

											{isIMenuButton(item) && (
												<Button
													onClick={item.onClick}
													variant={"default"}
													className={clsx(
														" hover:no-underline",
														"text-gray-100 text-sm",
														"group flex bg-opus-blue-100 text-left w-full px-2 py-2 w-36 mx-auto mt-5"
													)}
												>
													{item.label}
												</Button>
											)}
										</div>
									)
							)}
						</div>
					</Drawer>
				</div>
				<Dialog open={attendanceOpen} onOpenChange={setAttendanceOpen}>
					<DialogContent>
						<DialogHeader className="">
							<DialogTitle className="pt-4 w-full flex justify-between items-center">
								<span className="font-semibold text-xl  text-opus-gray-400">
									OPUS REMOTE
								</span>
								<span className="text-lg  text-opus-gray-400">{purpose}</span>
							</DialogTitle>
							<DialogClose onClick={close} />
						</DialogHeader>
						<Camera
							setOpen={setAttendanceOpen}
							isLatestTimeLogToday={isToday}
							latestTimeLog={latestTimeLog?.body}
							purpose={purpose}
							refetchLatestTimeLog={refetchLatestTimeLog}
						/>
						{/* <CameraMobile /> */}
					</DialogContent>
				</Dialog>
				<Modal title=" " isOpen={statusOpen} setOpen={setStatusOpen}>
					<UpdateStatus setStatusOpen={setStatusOpen} />
				</Modal>
			</div>
		</div>
	);
};

export default HeaderMobile;
